import { useRouter } from 'next/router'
import { useState } from 'react'
import ReactHtmlParser from 'react-html-parser'
import { HIGHLIGHT_METRIC, MetricsDetailItem } from '../../../../model/caf.interface'
import useIsMobile from '../../../../modules/hooks/common/useIsMobile'
import useHtmlParserTransform from '../../../../modules/hooks/webview/caf/common/useHtmlParserTransform'
import useRNMsg, { ringleAppNavs } from '../../../../modules/hooks/webview/common/useRNMsg'
import { n_caf } from '../../../../modules/i18n/strings/auto/wording_webview'
import useTranslation from '../../../../modules/i18n/useTranslation'
import { ICON_ALARM_GRAY } from '../../../../modules/svg/svg'
import ErrorReportPopupComponent from '../../../student/portal/lessons/caf/CAFOverview/popup/CafErrorReportPopup'
import Accordion from '../common/Accordion'
import AudioController from '../common/AudioController'
import AccordionContainer from './AccordionContainer'
import AccordionTitle from './AccordionTitle'

export interface AccordionProps {
  details: MetricsDetailItem[]
  page: {
    prev: number | null
    curr: number | null
    next: number | null
  }
}

const HighlightDetailListGeneral = ({ details, page }: AccordionProps) => {
  const router = useRouter()
  const { lessonId, tag } = router.query
  const { transformHTMLNode } = useHtmlParserTransform()

  const getMetric = (tagId) => {
    switch (+tagId) {
      case 15:
        return HIGHLIGHT_METRIC.SPEECH_RATE
      case 16:
        return HIGHLIGHT_METRIC.FILLER_WORD
      default:
        return HIGHLIGHT_METRIC.GRAMMAR
    }
  }

  return (
    <div className="mb-[12px]">
      <AccordionContainer>
        {details?.map((content, idx) => (
          <Accordion
            title={<AccordionTitle type={getMetric(tag)} content={content} index={idx + 1} tag={''} />}
            key={idx + 'component_item'}
            className="mb-[12px]"
            idx={idx}
          >
            {/* 아코디언 내용 */}
            <article className="mb-[8px] text-gray-700">
              <AccordionUpperContent type={getMetric(tag)} content={content} />
              {content.audio_url && <AudioController src={content.audio_url} id={idx} />}
              {+tag !== 16 && (
                <p className="text-sBody mt-[16px] rounded bg-gray-50 p-[8px] text-[14px] tracking-tight">
                  {ReactHtmlParser(content.sentence, {
                    transform: transformHTMLNode,
                  })}
                </p>
              )}
            </article>

            {/* 하단 도움말(오류제보, 인덱스 표시) */}
            <AccordionBottomContent type={getMetric(tag)} contentId={content.id} contentSentence={content.sentence} />
          </Accordion>
        ))}
      </AccordionContainer>
    </div>
  )
}

const AccordionUpperContent = ({ type, content }) => {
  const { t } = useTranslation()

  const formatMinSec = (time: number) => {
    const minutes = Math.floor(time / 60)
    const seconds = Math.floor(time % 60)
    return minutes + t(n_caf.highlight.min) + seconds + t(n_caf.highlight.sec)
  }
  const commonTimeInfo = (
    <p className="text-h5 mb-[4px] font-medium">
      {t(n_caf.highlight.time)}: {content.start_time} - {content.end_time} ({formatMinSec(content.duration_sec)})
    </p>
  )
  const products = {
    [HIGHLIGHT_METRIC.REPETITION]: <></>,
    [HIGHLIGHT_METRIC.SPEECH_RATE]: commonTimeInfo,
    [HIGHLIGHT_METRIC.FILLER_WORD]: (
      <>
        <p className="text-h5 mb-[16px] font-medium">
          {t(n_caf.highlight.filler_length)}: {Number(content.filler_duration || 0).toFixed(2)}초
        </p>
      </>
    ),
  }

  return products[type] || null
}

const AccordionBottomContent = ({ type, contentId, contentSentence }) => {
  const { t } = useTranslation()
  const { sendPostMsg } = useRNMsg()
  const [sentenceToReport, setSentenceToReport] = useState('')
  const [showPopup, setShowPopup] = useState(false)
  const isMobile = useIsMobile()

  const handleClickErrorReport = (contentId, contentSentence) => () => {
    if (isMobile) {
      sendPostMsg({
        nav: ringleAppNavs.cafErrorReport,
        detail_id: contentId,
        detail_content: contentSentence,
        from: 'highlight',
      })
    } else {
      setSentenceToReport(contentSentence)
    }
  }

  const products = {
    [HIGHLIGHT_METRIC.FILLER_WORD]: (
      <div
        className="flex h-[25px] cursor-pointer items-center text-[12px] font-medium tracking-wide text-gray-500 sm:text-[14px]"
        onClick={handleClickErrorReport(contentId, contentSentence)}
      >
        <img src={ICON_ALARM_GRAY} alt="alarm" className="mb-4" />
        <div className="ml-2 mt-1">{t(n_caf.highlight.report)}</div>
        <ErrorReportPopupComponent
          content={sentenceToReport}
          type="highlight"
          isShowEditPopup={showPopup}
          setShowEditPopup={setShowPopup}
          id={contentId}
        />
      </div>
    ),
  }

  return products[type] || null
}

export default HighlightDetailListGeneral
